import React, { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import { Box, Button, Card, CardContent, Divider, Skeleton, Typography } from '@mui/material'

import { GRTooltip } from '../../../../components/GRTooltip/GRTooltip'
import { EventLevelTooltipContent } from '../../../live-events/components/EventLevelTooltipContent'
import { CountChip } from '../CountChip'
import { EventCategoryOption, EventTypeOption } from '../helpers/catalogueUtils'
import { FILTER_CHIP_HEIGHT, FILTER_ROW_LABEL_WIDTH, filterBarSurfaceSx } from '../helpers/filterChipStyles'

interface EventFilterCardProps {
  eventCategoryOptions: EventCategoryOption[]
  eventTypeOptions: EventTypeOption[]
  selectedEventCategoryIds: string[]
  selectedEventTypeIds: string[]
  onToggleEventCategory: (id: string) => void
  onToggleEventType: (id: string) => void
  onClearEventCategories: () => void
  onClearEventTypes: () => void
  onClearEvents: () => void
  isLoading?: boolean
  // Caller-owned chrome below the chip rows, seamed off by the card's full-bleed
  // divider. The Data Glossary tab puts its blueprint-family search box here; it
  // filters the cards below the card, which this component knows nothing about,
  // so the query and the copy naming what it reaches stay with the caller.
  footerSlot?: ReactNode
  // Opens the event type row while a type is applied even though no category is picked, so a
  // type that outlived its category stays visible and clearable. Off by default, since with no
  // category picked the row is normally closed. The Live Event Analytics catalogue turns it on and
  // passes only the applied types for that case, so the row lists just those rather than every type.
  showTypeRowWhenTypeApplied?: boolean
}

const ROW_LABEL_SX = {
  fontSize: '0.75rem',
  textTransform: 'uppercase' as const,
  letterSpacing: '0.05em',
  color: 'text.secondary',
  fontWeight: 'bold',
  flexShrink: 0,
  width: FILTER_ROW_LABEL_WIDTH,
  minWidth: FILTER_ROW_LABEL_WIDTH,
  whiteSpace: 'nowrap' as const,
  // Match the chip height so the label lines up with the first chip row when the
  // chips wrap onto multiple lines (rows are aligned to flex-start).
  lineHeight: `${FILTER_CHIP_HEIGHT}px`,
}

// Outer row: fixed label column + chips column that wraps on its own, so every
// wrapped chip line starts under the first chip rather than under the label.
const ROW_SX = {
  display: 'flex',
  flexWrap: 'nowrap' as const,
  alignItems: 'flex-start',
  columnGap: 1,
}

const STACKED_ROW_SX = {
  ...ROW_SX,
  mt: 1.5,
  pt: 1.5,
  borderTop: '1px dashed',
  borderColor: 'divider',
}

const CHIPS_WRAP_SX = {
  display: 'flex',
  flexWrap: 'wrap' as const,
  alignItems: 'center',
  columnGap: 1,
  rowGap: 1,
  flex: '1 1 auto',
  minWidth: 0,
}

const SKELETON_WIDTHS = [120, 96, 108, 84, 132] as const

/**
 * Event category + event type filter chips, with the active-filter / clear
 * footer. Shared by the Live Event Analytics catalogue (below the segment card,
 * via `CatalogueFilterBar`) and the Data Glossary "Blueprint families" tab,
 * which renders it on its own with a search box in `footerSlot`.
 *
 * Chip counts are blueprint counts, derived client-side by the caller.
 */
export const EventFilterCard = React.memo(function EventFilterCard({
  eventCategoryOptions,
  eventTypeOptions,
  selectedEventCategoryIds,
  selectedEventTypeIds,
  onToggleEventCategory,
  onToggleEventType,
  onClearEventCategories,
  onClearEventTypes,
  onClearEvents,
  isLoading = false,
  footerSlot,
  showTypeRowWhenTypeApplied = false,
}: EventFilterCardProps) {
  const { t } = useTranslation()

  const allCategoriesSelected = selectedEventCategoryIds.length === 0
  const allTypesSelected = selectedEventTypeIds.length === 0
  const typeRowOpen = selectedEventCategoryIds.length > 0 || (showTypeRowWhenTypeApplied && selectedEventTypeIds.length > 0)
  const activeEventCount = selectedEventCategoryIds.length + selectedEventTypeIds.length

  return (
    // Same surface definition as the taxonomy bar (elevation 0 + divider border
    // when idle, primary-tinted border once a selection is active) so the two
    // cards read as one chrome zone and can't drift apart visually.
    <Card elevation={0} sx={filterBarSurfaceSx(activeEventCount > 0)}>
      <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
        {/* Event category row */}
        <Box sx={ROW_SX} role="group" aria-label={t('market-analytics:event_category')}>
          <GRTooltip content={<EventLevelTooltipContent level="category" showTitle={false} />} styles={{ display: 'inline-flex', flexShrink: 0 }}>
            <Typography component="span" sx={{ ...ROW_LABEL_SX, cursor: 'help' }}>
              {t('market-analytics:event_category')}
            </Typography>
          </GRTooltip>
          <Box sx={CHIPS_WRAP_SX}>
            {isLoading ? (
              SKELETON_WIDTHS.map((w, i) => (
                <Skeleton key={i} variant="rounded" width={w} height={FILTER_CHIP_HEIGHT} sx={{ borderRadius: `${FILTER_CHIP_HEIGHT / 2}px` }} />
              ))
            ) : (
              <>
                <CountChip label={t('market-analytics:all_event_categories')} selected={allCategoriesSelected} onClick={onClearEventCategories} />
                {eventCategoryOptions.map((opt) => (
                  <CountChip
                    key={opt.id}
                    label={opt.name}
                    count={opt.isCountPending ? undefined : opt.blueprintCount}
                    colorHex={opt.colorHex}
                    selected={selectedEventCategoryIds.includes(opt.id)}
                    value={opt.id}
                    onSelect={onToggleEventCategory}
                  />
                ))}
              </>
            )}
          </Box>
        </Box>

        {/* Event type row — cascades open only once at least one event category
            is picked (or, opt-in, while an event type is still applied). Dashed top border matches the taxonomy card. */}
        {!isLoading && typeRowOpen && eventTypeOptions.length > 0 ? (
          <Box sx={STACKED_ROW_SX} role="group" aria-label={t('market-analytics:event_type')}>
            <GRTooltip content={<EventLevelTooltipContent level="type" showTitle={false} />} styles={{ display: 'inline-flex', flexShrink: 0 }}>
              <Typography component="span" sx={{ ...ROW_LABEL_SX, cursor: 'help' }}>
                {t('market-analytics:event_type')}
              </Typography>
            </GRTooltip>
            <Box sx={CHIPS_WRAP_SX}>
              <CountChip label={t('market-analytics:all_event_types')} selected={allTypesSelected} onClick={onClearEventTypes} />
              {eventTypeOptions.map((opt) => (
                <CountChip
                  key={opt.id}
                  label={opt.name}
                  count={opt.isCountPending ? undefined : opt.blueprintCount}
                  colorHex={opt.colorHex}
                  selected={selectedEventTypeIds.includes(opt.id)}
                  value={opt.id}
                  onSelect={onToggleEventType}
                />
              ))}
            </Box>
          </Box>
        ) : null}

        {/* Clear-all footer — mirrors the taxonomy bar. Only shown when at
            least one event filter is active. */}
        {!isLoading && activeEventCount > 0 ? (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              columnGap: 1,
              mt: 1,
              pt: 1,
              borderTop: '1px dashed',
              borderColor: 'divider',
            }}
          >
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              {t('market-analytics:taxonomy_filters_active', { count: activeEventCount })}
            </Typography>
            <Button variant="text" size="small" onClick={onClearEvents} sx={{ '@media (pointer: coarse)': { minHeight: 44 } }}>
              {t('market-analytics:taxonomy_clear_all')}
            </Button>
          </Box>
        ) : null}

        {footerSlot ? (
          <>
            {/* Bled out to the card's edges (the negative inline margin cancels
                CardContent's horizontal padding) so the slot reads as its own
                band under the chip rows rather than as another row stacked with
                them. */}
            <Divider sx={{ mx: -2, my: 1.5 }} />
            {footerSlot}
          </>
        ) : null}
      </CardContent>
    </Card>
  )
})

EventFilterCard.displayName = 'EventFilterCard'
